import React, { useState, useEffect, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "react-axios";
import Skeleton from "react-loading-skeleton";

import { getSomePopularGenres, getRecentGenres, getGenreSearchResults, getShuffleTracks } from "../../api/getGenre";
import TrackBox from "../../Components/Elements/TrackBox";
import AddToPlaylistModal from "../../Components/Elements/AddToPlaylistModal";
import "./DiscoverPage.less";

const DISCOVER_SELECTED_GENRES_KEY = 'discover_selected_genres';

const DiscoverPage = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState(() => {
        // Load from sessionStorage on initial mount
        try {
            const stored = sessionStorage.getItem(DISCOVER_SELECTED_GENRES_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error("Error loading selected genres from sessionStorage:", error);
            return [];
        }
    });
    const [popularGenres, setPopularGenres] = useState([]);
    const [recentGenres, setRecentGenres] = useState([]);
    const [loadingPopular, setLoadingPopular] = useState(true);
    const [loadingRecent, setLoadingRecent] = useState(true);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [loadingTracks, setLoadingTracks] = useState(false);
    const [tracks, setTracks] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const selectedGenresRef = useRef(null);

    // Load popular genres on mount
    useEffect(() => {
        const loadPopularGenres = async () => {
            try {
                const response = await getSomePopularGenres(axios);
                setPopularGenres(response.data || []);
            } catch (error) {
                console.error("Error loading popular genres:", error);
            } finally {
                setLoadingPopular(false);
            }
        };
        loadPopularGenres();
    }, []);

    // Load recent genres on mount
    useEffect(() => {
        const loadRecentGenres = async () => {
            try {
                const response = await getRecentGenres(axios);
                setRecentGenres(response.data || []);
            } catch (error) {
                console.error("Error loading recent genres:", error);
            } finally {
                setLoadingRecent(false);
            }
        };
        loadRecentGenres();
    }, []);

    // Load tracks when selected genres change
    useEffect(() => {
        if (selectedGenres.length > 0) {
            loadTracks(1);
        } else {
            setTracks([]);
            setTotal(0);
        }
    }, [selectedGenres]);

    const handleSearch = async (e) => {
        e.preventDefault();
        const term = searchTerm.trim();
        if (term.length < 3) {
            setSearchResults([]);
            return;
        }

        setLoadingSearch(true);
        try {
            const response = await getGenreSearchResults(axios, term);
            const results = response.data || [];
            setSearchResults(results);
        } catch (error) {
            console.error("Error searching genres:", error);
            setSearchResults([]);
        } finally {
            setLoadingSearch(false);
        }
    };

    // Save selected genres to sessionStorage whenever they change
    useEffect(() => {
        try {
            if (selectedGenres.length > 0) {
                sessionStorage.setItem(DISCOVER_SELECTED_GENRES_KEY, JSON.stringify(selectedGenres));
            } else {
                sessionStorage.removeItem(DISCOVER_SELECTED_GENRES_KEY);
            }
        } catch (error) {
            console.error("Error saving selected genres to sessionStorage:", error);
        }
    }, [selectedGenres]);

    const addGenre = (genre) => {
        if (!selectedGenres.find((g) => g.id === genre.id)) {
            const updatedGenres = [...selectedGenres, genre];
            setSelectedGenres(updatedGenres);
            setSearchTerm("");
            setSearchResults([]);

            // Scroll to selected genres section
            setTimeout(() => {
                if (selectedGenresRef.current) {
                    selectedGenresRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 100);
        }
    };

    const removeGenre = (genreId) => {
        setSelectedGenres(selectedGenres.filter((g) => g.id !== genreId));
    };

    const loadTracks = async (pageNum = 1) => {
        if (selectedGenres.length === 0) return;

        setLoadingTracks(true);
        try {
            const genreIds = selectedGenres.map((g) => g.id);
            const response = await getShuffleTracks(axios, genreIds, pageNum, 20);
            const data = response.data || {};
            const newTracks = data.tracks || [];

            if (pageNum === 1) {
                setTracks(newTracks);
            } else {
                setTracks([...tracks, ...newTracks]);
            }

            setTotal(data.total || 0);
            setHasMore(newTracks.length === 20 && tracks.length + newTracks.length < data.total);
            setPage(pageNum);
        } catch (error) {
            console.error("Error loading tracks:", error);
        } finally {
            setLoadingTracks(false);
        }
    };

    const loadMore = () => {
        if (!loadingTracks && hasMore) {
            loadTracks(page + 1);
        }
    };

    return (
        <div className="discover-page-component">
            <Row>
                <Col xs={12}>
                    <div className="page-header">
                        {/* <h1>Discover New Music</h1> */}
                        <p className="lead"><strong>Discover</strong> tracks by genres you like, we'll help find you something nice.</p>
                    </div>
                </Col>
            </Row>

            <Row className="discover-search-section">
                <Col xs={12} md={6}>
                    <Form onSubmit={handleSearch}>
                        <Form.Group>
                            <Form.Label>Search for a style, genre or feeling</Form.Label>
                            <div className="d-flex">
                                <Form.Control
                                    type="text"
                                    placeholder="Enter genre name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                                <Button variant="primary" type="submit" className="ml-2" disabled={loadingSearch}>
                                    {loadingSearch ? "Searching..." : "Search"}
                                </Button>
                            </div>
                        </Form.Group>
                    </Form>

                    {searchResults.length === 0 && !loadingSearch && (
                        <div className="search-help-text mt-2">
                            <small className="text-muted">
                                Search for genres you are interested in, we will find you related tracks from our archive.
                                <br />
                                ex: AOR, Jazz, Funk, Soul, Disco, Baked, Electric, New Age, Cosmic etc.
                                <br /><br />Combine genres to expand your search for more fun.
                            </small>
                        </div>
                    )}

                    {searchResults.length > 0 && (
                        <div className="search-results mt-3">
                            <h5>Related Genres:</h5>
                            <div className="genre-suggestions">
                                {searchResults.map((genre) => (
                                    <Badge
                                        key={genre.id}
                                        pill
                                        variant="secondary"
                                        className="genre-badge m-1"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => addGenre(genre)}
                                    >
                                        {genre.genre || genre.name} {genre.total && `(${genre.total})`}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </Col>
                <Col xs={12} md={6}>
                    <div className="recent-genres-inline">
                        <h5>Recently Posted Genres</h5>
                        {loadingRecent ? (
                            <Skeleton count={3} />
                        ) : (
                            <div className="genre-suggestions">
                                {recentGenres.slice(0, 8).map((genre) => (
                                    <Badge
                                        key={genre.id}
                                        pill
                                        className="genre-badge genre-badge-recent m-1"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => addGenre(genre)}
                                    >
                                        {genre.genre} ({genre.total})
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="popular-genres-inline  mt-3">
                        <h5>Some Popular Genres</h5>
                        {loadingPopular ? (
                            <Skeleton count={3} />
                        ) : (
                            <div className="genre-suggestions">
                                {popularGenres.map((genre) => (
                                    <Badge
                                        key={genre.id}
                                        pill
                                        className="genre-badge genre-badge-popular m-1"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => addGenre(genre)}
                                    >
                                        {genre.genre} ({genre.total})
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>

                </Col>
            </Row>

            {selectedGenres.length > 0 && (
                <Row className="selected-genres-section mt-4" ref={selectedGenresRef}>
                    <Col xs={12}>
                        <h4>Selected Genres:</h4>
                        <div className="selected-genres">
                            {selectedGenres.map((genre) => (
                                <Badge
                                    key={genre.id}
                                    pill
                                    variant="primary"
                                    className="genre-badge m-1"
                                >
                                    {genre.genre || genre.name}
                                    <Button
                                        variant="link"
                                        className="p-0 ml-2 text-white"
                                        onClick={() => removeGenre(genre.id)}
                                        style={{ fontSize: "0.8em" }}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                        <button
                            className="clear-all-button mt-3"
                            onClick={() => setSelectedGenres([])}
                        >
                            Clear All
                        </button>
                    </Col>
                </Row>
            )}

            {selectedGenres.length > 0 && (
                <Row className="discover-tracks-section mt-4">
                    <Col xs={12}>
                        <h4>
                            Tracks ({total}){" "}
                            <small className="text-muted">Sorted by popularity</small>
                        </h4>
                        {loadingTracks && tracks.length === 0 ? (
                            <div className="loading">
                                <Skeleton count={5} />
                            </div>
                        ) : (
                            <>
                                <div className="tracks-grid">
                                    {tracks.map((track) => (
                                        <div key={track.id} className="track-item mb-4">
                                            <TrackBox track={track} showTitle={true} showDate={true} />
                                            <div className="track-stats mt-2">
                                                <small className="text-muted">
                                                    <FontAwesomeIcon icon="heart" className="mr-1" />
                                                    {track.favoriteCount || 0}
                                                    &nbsp;&nbsp;&nbsp;
                                                    <FontAwesomeIcon icon="comments" className="mr-1" />
                                                    {track.commentCount || 0}
                                                </small>
                                                <div className="track-item-actions">
                                                    <AddToPlaylistModal trackId={track.id} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {hasMore && (
                                    <div className="text-center mt-4">
                                        <Button variant="outline-primary" onClick={loadMore} disabled={loadingTracks}>
                                            {loadingTracks ? "Loading..." : "Load More"}
                                        </Button>
                                    </div>
                                )}
                                {tracks.length === 0 && !loadingTracks && (
                                    <p className="text-muted">No tracks found for selected genres.</p>
                                )}
                            </>
                        )}
                    </Col>
                </Row>
            )}

            {selectedGenres.length === 0 && (
                <Row className="mt-4">
                    <Col xs={12}>
                        <div className="text-center text-muted">
                            <p>Select genres above to discover new music!</p>
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default DiscoverPage;

